/**
 * FR-018.7 — React Query hooks over the farmer Sources service.
 */
import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  sourcesService,
  type CropTerm,
  type Duplicate,
  type MappingJob,
  type SourceSearchHit,
  type SourcesResponse,
  type Suggestion,
} from '../services/sourcesService';

export const sourcesKeys = {
  all: ['twin', 'sources'] as const,
  entity: (type: string, id: string) => ['twin', 'sources', type, id] as const,
  suggestions: (type: string, id: string) => ['twin', 'sources', type, id, 'suggestions'] as const,
  duplicates: (type: string, id: string) => ['twin', 'sources', type, id, 'duplicates'] as const,
  search: (type: string, q: string) => ['twin', 'sources', 'search', type, q] as const,
  job: (id: string) => ['twin', 'sources', 'job', id] as const,
};

const ACTIVE_JOB = new Set(['planned', 'queued', 'running']);

export function useEntitySources(type: string, id: string, enabled = true) {
  return useQuery<SourcesResponse>({
    queryKey: sourcesKeys.entity(type, id),
    queryFn: () => (type === 'crop' ? sourcesService.getTermSources(type, id) : sourcesService.getSources(type, id)),
    enabled,
  });
}

export function useSourceSuggestions(type: string, id: string, enabled = true) {
  return useQuery<Suggestion[]>({
    queryKey: sourcesKeys.suggestions(type, id),
    queryFn: () => sourcesService.getSuggestions(type, id),
    enabled,
  });
}

export function useSourceDuplicates(type: string, id: string, enabled = true) {
  return useQuery<Duplicate[]>({
    queryKey: sourcesKeys.duplicates(type, id),
    queryFn: () => sourcesService.getDuplicates(type, id),
    enabled,
  });
}

export function useSourceSearch(type: string, q: string) {
  return useQuery<SourceSearchHit[]>({
    queryKey: sourcesKeys.search(type, q),
    queryFn: () => sourcesService.search(type, q),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });
}

export function useSourcePreview() {
  return useMutation({ mutationFn: sourcesService.preview });
}

export function useSourceConfirm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sourcesService.confirm,
    onSuccess: () => {
      // The re-home changes entities, so every twin surface is stale.
      queryClient.invalidateQueries({ queryKey: sourcesKeys.all });
      queryClient.invalidateQueries({ queryKey: ['twin'] });
    },
  });
}

export function useSourceReject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sourcesService.reject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourcesKeys.all });
    },
  });
}

export function useSourceMerge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sourcesService.merge,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['twin'] });
    },
  });
}

/**
 * A re-home job's status, polled every 3 s while it is active. When it reaches `done` the
 * Sources rows (and every twin surface) are stale, so they are invalidated once — the job's
 * own query is left alone so it is not fetched again.
 */
/** Everything twin is stale except the job records themselves (re-fetching those is pointless). */
const staleAfterRehome = (q: { queryKey: readonly unknown[] }) =>
  q.queryKey[0] === 'twin' && !(q.queryKey[1] === 'sources' && q.queryKey[2] === 'job');

/** A callback that re-fetches the Sources rows and every twin surface (not the job records). */
export function useInvalidateSources() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ predicate: staleAfterRehome });
  }, [queryClient]);
}

const TERMINAL_JOB = new Set(['done', 'failed', 'refused']);

export function useMappingJob(jobId: string | null, { pollWhileActive = true }: { pollWhileActive?: boolean } = {}) {
  const queryClient = useQueryClient();
  const query = useQuery<MappingJob>({
    queryKey: sourcesKeys.job(jobId ?? ''),
    queryFn: () => sourcesService.getJob(jobId as string),
    enabled: jobId !== null,
    refetchInterval: (query) =>
      pollWhileActive && query.state.data && ACTIVE_JOB.has(query.state.data.status) ? 3000 : false,
  });
  const status = query.data?.status;
  useEffect(() => {
    // `failed` / `refused` too: a job checkpoints per unit, so units may have moved before it stopped.
    // The effect keys on the status, so each terminal transition invalidates once.
    if (!status || !TERMINAL_JOB.has(status)) return;
    queryClient.invalidateQueries({ predicate: staleAfterRehome });
  }, [status, jobId, queryClient]);
  return query;
}

/** FR-018.7 §8 — the crop terms in use (live crop targets ∪ distinct `crop_cycle.crop`). */
export function useCropTerms(enabled = true) {
  return useQuery<CropTerm[]>({
    queryKey: [...sourcesKeys.all, 'terms', 'crop'] as const,
    queryFn: () => sourcesService.getCropTerms(),
    enabled,
  });
}
